import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Smartphone, Bell, Sparkles, Radio,
  AppWindow, PhoneCall, MapPinOff, Frown, AlertCircle
} from 'lucide-react';
import landingData from '../landingData.json';

const ProblemSection = () => {
  const [isResolved, setIsResolved] = useState(false);

  const withoutTrripIcons = [
    <AppWindow key="0" size={32} strokeWidth={1.5} className="text-[#be4b46]" />,
    <PhoneCall key="1" size={32} strokeWidth={1.5} className="text-[#be4b46]" />,
    <MapPinOff key="2" size={32} strokeWidth={1.5} className="text-[#be4b46]" />,
    <Frown key="3" size={32} strokeWidth={1.5} className="text-[#be4b46]" />
  ];

  const withTrripIcons = [
    <Smartphone key="0" size={32} strokeWidth={1.5} className="text-[#067978]" />,
    <Bell key="1" size={32} strokeWidth={1.5} className="text-[#067978]" />,
    <Sparkles key="2" size={32} strokeWidth={1.5} className="text-[#067978]" />,
    <Radio key="3" size={32} strokeWidth={1.5} className="text-[#067978]" />
  ];

  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6 md:px-12 lg:px-24 bg-gradient-to-b from-[#f8fafc] to-white relative overflow-hidden flex items-center" id="problem">
      {/* Background Decorative Elements */}
      <div className={`absolute top-0 left-0 w-full h-[500px] transition-colors duration-1000 -z-10 ${isResolved ? 'bg-gradient-to-b from-[#067978]/5 to-transparent' : 'bg-gradient-to-b from-gray-100 to-transparent'}`}></div>
      <div className={`absolute -top-[200px] -right-[200px] w-[600px] h-[600px] rounded-full blur-[100px] -z-10 pointer-events-none transition-colors duration-1000 ${isResolved ? 'bg-[#067978]/10' : 'bg-gray-200/50'}`}></div>

      <div className="max-w-7xl mx-auto w-full flex flex-col items-center">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-10 sm:mb-12 relative z-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#067978]/20 bg-[#067978]/10 text-[#067978] font-bold text-[10px] uppercase tracking-widest mb-2">
            <AlertCircle size={14} /> THE VISIBILITY GAP
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1a2b49] tracking-tight leading-tight transition-all duration-700">
            You're right where they're looking. <br className="hidden sm:inline" />
            <span className="text-[#067978]">But they can't find you.</span>
          </h2>

          <p className="text-[#067978] text-xs sm:text-base md:text-lg font-normal transition-all duration-700 max-w-2xl mx-auto pt-1 sm:pt-2">
            Travellers walk past your door every day. Without the right visibility, they'll never know you're there.
          </p>
        </div>

        {/* Massive iOS-Style Toggle Switch */}
        <div className="relative z-30 mb-10 sm:mb-14 flex items-center justify-center">
          <div className="flex bg-[#fdfaf5] p-1.5 sm:p-2 rounded-full relative shadow-inner border border-[#e5d8c8] backdrop-blur-sm">
            {/* Animated Slider Background */}
            <div
              className={`absolute top-1.5 bottom-1.5 sm:top-2 sm:bottom-2 w-[calc(50%-6px)] sm:w-[calc(50%-8px)] rounded-full transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                isResolved ? 'left-[calc(50%+3px)] sm:left-[calc(50%+4px)] bg-[#067978] shadow-sm' : 'left-1.5 sm:left-2 bg-[#be4b46] shadow-sm'
              }`}
            ></div>

            {/* Left Toggle (Without Trrip) */}
            <button
              type="button"
              onClick={() => setIsResolved(false)}
              className={`relative w-32 sm:w-40 md:w-48 py-3 sm:py-3.5 text-[11px] sm:text-xs md:text-sm font-bold uppercase tracking-widest transition-colors duration-300 z-10 rounded-full focus:outline-none cursor-pointer ${
                !isResolved ? 'text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Without Trrip
            </button>

            {/* Right Toggle (With Trrip) */}
            <button
              type="button"
              onClick={() => setIsResolved(true)}
              className={`relative w-32 sm:w-40 md:w-48 py-3 sm:py-3.5 text-[11px] sm:text-xs md:text-sm font-bold uppercase tracking-widest transition-colors duration-300 z-10 rounded-full focus:outline-none cursor-pointer ${
                isResolved ? 'text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              With Trrip
            </button>
          </div>
        </div>

        {/* Content Area - AnimatePresence handles the Blur In/Out transition */}
        <div className="w-full relative">
          <AnimatePresence mode="wait">
            {!isResolved ? (
              /* PROBLEMS GRID (All 4 Items Fully Visible) */
              <motion.div
                key="problems"
                initial={{ opacity: 0, filter: "blur(12px)", scale: 0.96 }}
                animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
                exit={{ opacity: 0, filter: "blur(12px)", scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 md:gap-8 w-full"
              >
                {landingData.withoutTrripItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="group bg-white border border-gray-100 rounded-[1.5rem] p-6 sm:p-8 flex flex-col items-center text-center shadow-sm hover:shadow-lg transition-all duration-500 relative overflow-hidden"
                  >
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-rose-50 flex items-center justify-center mb-5 sm:mb-6 relative z-10 transform group-hover:scale-110 transition-transform duration-500">
                      {withoutTrripIcons[idx % withoutTrripIcons.length]}
                    </div>
                    <h3 className="text-[#1a2b49] font-bold text-base sm:text-lg mb-2.5 relative z-10">{item.title}</h3>
                    <p className="text-gray-500 text-xs sm:text-[13px] leading-relaxed relative z-10 font-normal">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </motion.div>
            ) : (
              /* WITH TRRIP GRID (All 4 Items Fully Visible) */
              <motion.div
                key="solutions"
                initial={{ opacity: 0, filter: "blur(12px)", scale: 0.96 }}
                animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
                exit={{ opacity: 0, filter: "blur(12px)", scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 md:gap-8 w-full"
              >
                {landingData.withTrripItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="group bg-white border-t-4 border-t-[#067978] border border-gray-100 rounded-[1.5rem] p-6 sm:p-8 flex flex-col items-center text-center shadow-[0_4px_20px_rgba(6,121,120,0.04)] hover:shadow-[0_10px_40px_rgba(6,121,120,0.1)] transition-all duration-500 relative overflow-hidden"
                  >
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#cfe6da]/60 flex items-center justify-center mb-5 sm:mb-6 relative z-10 transform group-hover:scale-110 transition-transform duration-500">
                      {withTrripIcons[idx % withTrripIcons.length]}
                    </div>
                    <h3 className="text-[#1a2b49] font-bold text-base sm:text-lg mb-2.5 relative z-10">{item.title}</h3>
                    <p className="text-gray-500 text-xs sm:text-[13px] leading-relaxed relative z-10 font-normal">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
